import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { FiExternalLink } from "react-icons/fi";

const ServiceCard = ({ service, index }: any) => {
  const navigate = useNavigate();
  
  const handleClick = () => {
    navigate(`/services/${service.name.toLowerCase().replace(/\s+/g, "-")}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
      className="relative group cursor-pointer"
      onClick={handleClick}
    >
      <div className="relative overflow-hidden rounded-2xl bg-[#1C1C1C] p-8 h-[400px] transition-all duration-300 
        hover:shadow-2xl hover:shadow-white/10 hover:scale-[1.02]">
        {/* Card Content */}
        <div className="relative z-10 h-full flex flex-col">
          {/* Header with External Link Icon */}
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-3xl font-semibold">{service.name}</h3>
            <motion.div
              whileHover={{ scale: 1.1 }}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <FiExternalLink className="text-2xl" />
            </motion.div>
          </div>

          <p className="text-gray-400 font-mono text-sm mb-8">{service.description}</p>

          {/* 3D Element Container */}
          <div className="flex-grow flex items-center justify-center">
            <div className="relative w-48 h-48">
              <motion.div
                className={`absolute inset-0 ${service.color} blur-2xl opacity-20`}
                animate={{
                  scale: [1, 1.2, 1],
                  rotate: [0, 90, 0],
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
              <motion.div
                className={`absolute inset-0 bg-gradient-to-r ${service.color} rounded-xl`}
                animate={{
                  rotateX: [0, 360],
                  rotateY: [0, 360],
                }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  ease: "linear"
                }}
                style={{
                  transformStyle: "preserve-3d",
                  transform: "perspective(1000px)",
                }}
              />
            </div>
          </div>
        </div>

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/50" />

        {/* Hover Effect - Gradient Border */}
        <div className="absolute inset-0 rounded-3xl p-[1px] bg-gradient-to-b from-transparent via-white/5 to-white/10 
          opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>
    </motion.div>
  );
};

const Services = () => {
  const services = [
    {
      name: "AI Agents",
      description: "AI-powered autonomous agents designed to perform tasks and optimize workflows.",
      color: "from-[#FF3366] via-[#FF6B33] to-[#FFB333]"
    },
    {
      name: "Face Recognition",
      description: "Advanced AI for facial analysis and identity verification.",
      color: "from-[#33FF8C] via-[#33FFD4] to-[#33C7FF]"
    },
    {
      name: "OCR",
      description: "Extract text from images and scanned documents effortlessly.",
      color: "from-[#8C33FF] via-[#D433FF] to-[#FF33F5]"
    },
    {
      name: "Object Detection",
      description: "Identify and track objects seamlessly using AI.",
      color: "from-[#FF33A8] via-[#FF338C] to-[#FF3366]"
    },
    {
      name: "Text Summarization",
      description: "AI that converts lengthy text into concise summaries.",
      color: "from-[#33FFF5] via-[#33D4FF] to-[#338CFF]"
    },
    {
      name: "Language Translation",
      description: "Real-time AI-powered multilingual translations.",
      color: "from-[#FFB333] via-[#FF8C33] to-[#FF6B33]"
    },
    {
      name: "Sentiment Analysis",
      description: "Analyze emotions in text and customer interactions.",
      color: "from-[#33FF66] via-[#33FF8C] to-[#33FFB3]"
    },
    {
      name: "Chatbots",
      description: "AI-powered chat assistants for real-time engagement.",
      color: "from-[#B333FF] via-[#8C33FF] to-[#6B33FF]"
    },
    {
      name: "Recommendation Systems",
      description: "Personalized AI-driven content recommendations.",
      color: "from-[#FF3366] via-[#FF33A8] to-[#FF33F5]"
    },
    {
      name: "Time Series Forecasting",
      description: "AI-driven predictions for future trends and analytics.",
      color: "from-[#33FFD4] via-[#33B3FF] to-[#3366FF]"
    }
];

  return (
    <div className="min-h-screen bg-black text-white px-6 py-16">
      {/* Header Section */}
      <motion.div
        className="text-center mb-20"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-5xl md:text-6xl mb-6">
          Our Services
        </h1>
        <p className="text-gray-400 max-w-2xl mx-auto">
          Experience the next generation of AI solutions
        </p>
      </motion.div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {services.map((service, index) => (
          <ServiceCard key={index} service={service} index={index} />
        ))}
      </div>
    </div>
  );
};

export default Services;
























// import React from "react";
// import { motion } from "framer-motion";
// import { FiExternalLink } from "react-icons/fi";
// import { Link } from "react-router-dom";

// const services = [
//   { name: "AI Agents", description: "AI-powered autonomous agents designed to perform tasks and optimize workflows." },
//   { name: "Face Recognition", description: "Advanced AI for facial analysis and identity verification." },
//   { name: "OCR", description: "Extract text from images and scanned documents effortlessly." },
//   { name: "Object Detection", description: "Identify and track objects seamlessly using AI." },
//   { name: "Text Summarization", description: "AI that converts lengthy text into concise summaries." },
//   { name: "Language Translation", description: "Real-time AI-powered multilingual translations." },
//   { name: "Sentiment Analysis", description: "Analyze emotions in text and customer interactions." },
//   { name: "Chatbots", description: "AI-powered chat assistants for real-time engagement." },
//   { name: "Recommendation Systems", description: "Personalized AI-driven content recommendations." },
//   { name: "Time Series Forecasting", description: "AI-driven predictions for future trends and analytics." },
// ];

// const Services = () => {
//   return (
//     <div className="min-h-screen bg-black text-white px-6 md:px-16 py-16">
//       {/* Page Header */}
//       <motion.div
//         className="text-center mb-12"
//         initial={{ opacity: 0, y: -50 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ duration: 1 }}
//       >
//         <p className="text-4xl md:text-5xl">Our Services</p>
//         <p className="text-lg text-gray-400 mt-4 max-w-2xl mx-auto">
//           Explore our cutting-edge AI solutions designed to revolutionize industries.
//         </p>
//       </motion.div>

//       {/* Services List - One Below Another */}
//       <div className="container mx-auto flex flex-col space-y-10">
//         {services.map((service, index) => (
//           <motion.div
//             key={index}
//             className="bg-black p-6 rounded-2xl shadow-lg transition-transform duration-300 hover:scale-105"
//             initial={{ opacity: 0, y: 50 }}
//             whileInView={{ opacity: 1, y: 0 }}
//             transition={{
//               type: "spring",
//               stiffness: 80,
//               damping: 15,
//               delay: index * 0.1,
//             }}
//             viewport={{ once: false }}
//           >
//             <div className="flex justify-between items-center">
//               <p className="text-2xl md:text-3xl">{service.name}</p>
//               <Link to={`/services/${service.name.toLowerCase().replace(/\s+/g, "-")}`}>
//                 <FiExternalLink className="text-2xl text-gray-500 hover:text-white transition duration-300" />
//               </Link>
//             </div>
//             <p className="mt-4 text-gray-400 text-lg">{service.description}</p>
//           </motion.div>
//         ))}
//       </div>
//     </div>
//   );
// };

// export default Services;








// import React from "react";
// import { motion } from "framer-motion";
// import { FiExternalLink } from "react-icons/fi";
// import { Link } from "react-router-dom";

// const services = [
  // {
  //   name: "AI Agents",
  //   description:
  //     "AI-powered autonomous agents designed to perform specific tasks, adapt to user needs, and optimize workflows seamlessly.",
  // },
//   { name: "Face Recognition", description: "Advanced AI for facial analysis" },
//   { name: "OCR", description: "Text extraction from images" },
//   { name: "Object Detection", description: "Identifying objects seamlessly" },
//   { name: "Text Summarization", description: "Turning text into key ideas" },
//   { name: "Language Translation", description: "AI-powered translation" },
//   { name: "Sentiment Analysis", description: "Understanding emotions in data" },
//   { name: "Chatbots", description: "Interactive AI for customer engagement" },
//   { name: "Recommendation Systems", description: "Personalized content delivery" },
//   { name: "Time Series Forecasting", description: "AI predictions for future trends" },
// ];

// const Services = () => {
//   return (
//     <div
//       className="min-h-screen bg-black flex flex-col items-center justify-center px-4 py-16"
//     >

//       <motion.div
//         className="text-center mb-12 pt-16"
//         initial={{ opacity: 0, y: -50 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ duration: 1 }}
//       >
//         <h1 className="text-4xl md:text-5xl font-extrabold text-white">Our Services</h1>
//         <p className="text-gray-300 text-sm md:text-lg mt-4 max-w-2xl mx-auto">
//           Explore our cutting-edge AI solutions designed to revolutionize industries.
//         </p>
//       </motion.div>
//       <div className="grid grid-cols-1 gap-6 w-full max-w-4xl">
//         {services.map((service, index) => (
//           <motion.div
//             key={index}
//             className="relative bg-black/40 backdrop-blur-lg rounded-lg p-6 shadow-lg transition-transform duration-300 hover:scale-105 hover:shadow-2xl"
//             initial={{ opacity: 0, y: 50 }}
//             whileInView={{ opacity: 1, y: 0 }}
//             transition={{
//               type: "spring",
//               stiffness: 80,
//               damping: 15,
//               delay: index * 0.1,
//             }}
//             viewport={{ once: true }}
//           >
//             <div className="flex justify-between items-center">
//               <h2 className="text-lg md:text-2xl font-bold text-white">{service.name}</h2>
//               <Link
//                 to={`/services/${service.name.toLowerCase().replace(/\s+/g, "-")}`}
//                 className="text-gray-400 hover:text-white"
//               >
//                 <FiExternalLink className="text-xl md:text-2xl" />
//               </Link>
//             </div>
//             <p className="text-gray-300 text-sm md:text-base mt-2">{service.description}</p>
//           </motion.div>
//         ))}
//       </div>
//     </div>
//   );
// };

// export default Services;


// import React from "react";
// import { motion } from "framer-motion";
// import { FiExternalLink } from "react-icons/fi";
// import { Link } from "react-router-dom";

// const services = [
//   {
//     name: "AI Agents",
//     description:
//       "AI-powered autonomous agents designed to perform specific tasks, adapt to user needs, and optimize workflows seamlessly.",
//   },
//   { name: "Face Recognition", description: "Advanced AI for facial analysis" },
//   { name: "OCR", description: "Text extraction from images" },
//   { name: "Object Detection", description: "Identifying objects seamlessly" },
//   { name: "Text Summarization", description: "Turning text into key ideas" },
//   { name: "Language Translation", description: "AI-powered translation" },
//   { name: "Sentiment Analysis", description: "Understanding emotions in data" },
//   { name: "Chatbots", description: "Interactive AI for customer engagement" },
//   { name: "Recommendation Systems", description: "Personalized content delivery" },
//   { name: "Time Series Forecasting", description: "AI predictions for future trends" },
// ];

// const Services = () => {
//   return (
//     <div className="min-h-screen bg-black text-white">
//       <motion.div
//         className="text-center py-20"
//         initial={{ opacity: 0, y: -50 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ duration: 1 }}
//       >
//         <p className="text-5xl ">Our Services</p>
//         <p className="text-lg text-gray-400 mt-4">
//           Explore our cutting-edge AI solutions designed to revolutionize industries.
//         </p>
//       </motion.div>

//       <div className="container mx-auto px-6 pb-20">
//         {services.map((service, index) => (
//           <motion.div
//             key={index}
//             className="mb-16"
//             initial={{ opacity: 0, y: 50 }}
//             whileInView={{ opacity: 1, y: 0 }}
//             transition={{
//               type: "spring",
//               stiffness: 80,
//               damping: 15,
//               delay: index * 0.1,
//             }}
//             viewport={{ once: false }}
//           >
//             <div className="flex justify-between items-center">
//               <p className="text-3xl md:text-4xl">{service.name}</p>
//               <Link to={`/services/${service.name.toLowerCase().replace(/\s+/g, "-")}`}>
//                 <FiExternalLink className="text-2xl text-gray-500 hover:text-white transition duration-300" />
//               </Link>
//             </div>
//             <p className="mt-4 text-gray-400 text-lg">{service.description}</p>
//           </motion.div>
//         ))}
//       </div>
//     </div>
//   );
// };

// export default Services;
